import { compactAmount, NUMBER_LOCALE, usdHeading } from "./format";

// KPI-tile wrappers over the shared affiliate formatters. Values render at subheading.xl (32px) in a
// DividedBanner cell that is a fraction of the page width, so a raw float sum would overflow.

const EMPTY = "—";

/** Grouped digits stay narrower than a compact form below a million ("999,999" < "999.99K"). */
const COUNT_COMPACT_THRESHOLD = 1_000_000;

/** USD magnitudes (volume, earnings): "$3.54M". */
export function formatKpiUsd(value: number): string {
  return Number.isFinite(value) ? usdHeading(value) : EMPTY;
}

/** Amps balances: "30.08K". */
export function formatKpiAmps(value: number): string {
  return Number.isFinite(value) ? compactAmount(value) : EMPTY;
}

/**
 * Counts (sign-ups, referrals): exact up to a million, compact past it.
 * Small counts must not read as quantities — 8 sign-ups is "8", never "8.00".
 */
export function formatKpiCount(value: number): string {
  if (!Number.isFinite(value)) return EMPTY;
  if (Math.abs(value) >= COUNT_COMPACT_THRESHOLD) return compactAmount(value);
  return Math.round(value).toLocaleString(NUMBER_LOCALE, { maximumFractionDigits: 0 });
}

export interface KpiCoverage {
  /** True when the figure was summed over fewer rows than the server counted. */
  partial: boolean;
  shown: number;
  total: number;
}

/**
 * Whether a tile summed the whole set or only the rows the client holds.
 *
 * The tiles that reduce over `earnings.users.items` are bounded by the SDK's hardcoded
 * `limit: "1000"`, while `total_count` beside those items is the real figure — counted
 * before the handler paginates. The largest downline measured is 17,348, so the top
 * affiliates' tiles cover under 6% of their referrals; a tile that cannot be exact should
 * say so rather than print a confident number that is wrong seventeenfold.
 */
export function kpiCoverage(shown: number, totalCount: number | null | undefined): KpiCoverage {
  // Absent counts read as complete: treating a missing `total_count` as 0 would flag
  // every tile as truncated.
  const total = typeof totalCount === "number" && Number.isFinite(totalCount) ? totalCount : shown;
  return { partial: total > shown, shown, total: Math.max(total, shown) };
}

/** A sum over a truncated set is a lower bound, so it is rendered as one: "≥ $3.54M". */
export function formatKpiFloor(formatted: string, partial: boolean): string {
  if (!partial || formatted === EMPTY) return formatted;
  return `≥ ${formatted}`;
}
